class CartManager {
	constructor(el, context = 'cart') {
		this.el = el;
		this.context = context;
		this.isUpdating = false;

		if (this.el.hasAttribute('data-cart-manager-initialized')) return;
		this.el.setAttribute('data-cart-manager-initialized', 'true');

		this.content = this.el.querySelector(
			this.context === 'cart' ? '[data-cart-page-content]' : '[data-cart-drawer-content]'
		);

		this.bindEvents();
	}

	bindEvents() {
		// Gestion des boutons +/-
		this.el.addEventListener('click', (e) => {
			const key = e.target.getAttribute('data-key');
			if (!key) return;

			if (e.target.matches('[data-cart-qty-plus]')) {
				this.handleQuantityChange(key, 'increment');
			} else if (e.target.matches('[data-cart-qty-minus]')) {
				this.handleQuantityChange(key, 'decrement');
			} else if (e.target.matches('[data-cart-remove]')) {
				this.updateCart(key, 0);
			}
		});

		// Gestion des inputs de quantité
		this.el.addEventListener('change', (e) => {
			if (!e.target.matches('[data-cart-qty]')) return;
			
			const key = e.target.getAttribute('data-key');
			const value = Math.max(0, parseInt(e.target.value, 10) || 0);
			e.target.value = value;
			
			this.updateCart(key, value);
		});

		// Gestion des boutons de quantité produit
		this.el.addEventListener('click', (e) => {
			if (e.target.matches('[data-product-qty-plus]') || e.target.closest('[data-product-qty-plus]')) {
				this.handleProductQuantity(e.target, 1);
			} else if (e.target.matches('[data-product-qty-minus]') || e.target.closest('[data-product-qty-minus]')) {
				this.handleProductQuantity(e.target, -1);
			}
		});
	}

	handleQuantityChange(key, action) {
		const input = this.el.querySelector(`[data-cart-qty][data-key="${key}"]`);
		if (!input) return;

		const currentValue = parseInt(input.value, 10) || 1;
		const newValue = action === 'increment' 
			? currentValue + 1 
			: Math.max(0, currentValue - 1);

		input.value = newValue;
		this.updateCart(key, newValue);
	}

	handleProductQuantity(target, increment) {
		const button = target.closest('[data-product-qty-plus], [data-product-qty-minus]');
		const input = button?.parentElement.querySelector('[data-product-qty-input]');
		if (!input) return;

		const currentValue = parseInt(input.value, 10) || 1;
		const newValue = Math.max(1, currentValue + increment);
		
		input.value = newValue;
		input.dispatchEvent(new Event('change'));
	}

	async updateCart(key, quantity) {
		if (this.isUpdating) return;
		
		this.isUpdating = true;
		this.setItemLoading(key, true);

		try {
			// await new Promise(resolve => setTimeout(resolve, 300)); // Délai visuel
			await this.changeCartItem(key, quantity);
			await this.refreshSection();
		} catch (error) {
			console.error('Cart update error:', error);
		} finally {
			this.setItemLoading(key, false);
			this.isUpdating = false;
		}
	}

	async changeCartItem(key, quantity) {
		const formData = new FormData();
		formData.append('id', key);
		formData.append('quantity', String(quantity));

		await fetch('/cart/change.js', {
			method: 'POST',
			headers: { 'Accept': 'application/json' },
			body: formData
		});

		document.dispatchEvent(new CustomEvent('cart:item-updated', { 
			detail: { key, quantity } 
		}));

		await this.updateCartCount();
	}

	async refreshSection() {
		try {
			const response = await fetch(`/cart?section_id=${this.context}`);
			const html = await response.text();
			
			const temp = document.createElement('div');
			temp.innerHTML = html;
			const selector = this.context === 'cart' ? '[data-cart-page-content]' : '[data-cart-drawer-content]';
			const newContent = temp.querySelector(selector);
			console.log('[CartManager] refreshSection:', { selector, thisContent: this.content, newContent });
			if (!newContent) {
				console.warn('[CartManager] refreshSection: newContent is null for selector', selector);
			}
			if (!this.content) {
				console.warn('[CartManager] refreshSection: this.content is null');
			}
			if (newContent && this.content) {
				this.content.innerHTML = newContent.innerHTML;
			}
		} catch (error) {
			console.error('Section refresh error:', error);
		}
	}

	setItemLoading(key, isLoading) {
		const item = document.querySelector(`[data-cart-item][data-key="${key}"]`);
		if (!item) return;

		const loadingBar = item.querySelector('[data-cart-loading-bar]');
		const actionsContainer = item.querySelector('[data-cart-item-actions]');
		
		if (!loadingBar) return;

		if (isLoading) {
			// Désactiver les actions avec une classe
			if (actionsContainer) {
				actionsContainer.classList.add('disabled');
			}

			// Animation d'entrée avec GSAP
			gsap.timeline()
				.to(loadingBar, {
					opacity: 1,
					duration: 0.1,
					transformOrigin: "0% 50%",
					ease: "power2.out"
				})
				.to(loadingBar, {
					scaleX: 1,
					duration: 0.4,
					ease: "power2.out"
				}, 0.05);
		} else {
			// Réactiver les actions
			if (actionsContainer) {
				actionsContainer.classList.remove('disabled');
			}
			// Animation de sortie pour la loading bar
			gsap.timeline()
				.to(loadingBar, {
					scaleX: 0,
					duration: 0.3,
					transformOrigin: "100% 50%",
					ease: "power2.in"
				})
		}
	}

	async updateCartCount() {
		try {
			const response = await fetch('/cart.js');
			if (!response.ok) return;
			
			const cart = await response.json();
			
			document.querySelectorAll('[data-cart-count]').forEach(element => {
				element.textContent = cart.item_count;
				element.setAttribute('aria-label', `${cart.item_count} items in cart`);
			});
		} catch (error) {
			console.error('Update cart count error:', error);
		}
	}
}

// Gestionnaire simple pour l'ajout au panier

class AddToCartHandler {
	constructor() {
		this.bindEvents();
	}

	bindEvents() {
		document.addEventListener('submit', (e) => this.handleSubmit(e));
	}

	async handleSubmit(e) {
		const form = e.target;
		const addButton = form.querySelector('[data-add-to-cart]');
		
		if (!addButton) return;

		e.preventDefault();
		
		// --- Vérification du stock max ---
		const stockMax = parseInt(addButton.dataset.stock, 10) || 9999;
		const variantId = form.querySelector('[name="id"]').value;
		const qtyInput = form.querySelector('[name="quantity"]');
		const qtyDemandee = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

		// Récupérer la quantité déjà dans le panier pour ce variant
		let dejaDansPanier = 0;
		try { 
			const cartRes = await fetch('/cart.js');
			if (cartRes.ok) {
				const cart = await cartRes.json();
				const item = cart.items.find(i => String(i.variant_id) === String(variantId));
				if (item) dejaDansPanier = item.quantity;
			}
		} catch (err) {
			// fail silently, on laisse passer l'ajout si erreur
		}

		const errorDiv = form.querySelector('[data-add-to-cart-error]');
		if (errorDiv) {
			errorDiv.classList.add('hidden');
			clearTimeout(errorDiv._hideTimeout);
		}

		// --- Gestion du stock illimité ---
		const isStockUnlimited = stockMax < 0;

		if (!isStockUnlimited && (dejaDansPanier + qtyDemandee > stockMax)) {
			if (errorDiv) {
				errorDiv.classList.remove('hidden');
				clearTimeout(errorDiv._hideTimeout);
				errorDiv._hideTimeout = setTimeout(() => {
					errorDiv.classList.add('hidden');
				}, 2000);
			}
			return;
		}

		this.setButtonLoading(addButton, true);

		try {
			await this.addToCart(new FormData(form));
			await this.openCartDrawer();
		} catch (error) {
			console.error('Add to cart error:', error);
		} finally {
			this.setButtonLoading(addButton, false);
		}
	}

	async addToCart(formData) {
		const response = await fetch('/cart/add.js', {
			method: 'POST',
			headers: { 'Accept': 'application/json' },
			body: formData
		});
		
		if (!response.ok) {
			throw new Error('Add to cart failed');
		}
		
		return response;
	}

	async openCartDrawer() {
		// Ouvrir le drawer si disponible
		if (window.drawerUIInstance?.open) {
			window.drawerUIInstance.open();
			
			// Rafraîchir le contenu du drawer
			if (window.drawerUIInstance.el?.CartManagerInstance) {
				await window.drawerUIInstance.el.CartManagerInstance.refreshSection();
			}
		}
		
		// Mettre à jour le compteur
		await this.updateCartCount();
	}

	async updateCartCount() {
		try {
			const response = await fetch('/cart.js');
			if (!response.ok) return;
			
			const cart = await response.json();
			
			document.querySelectorAll('[data-cart-count]').forEach(element => {
				element.textContent = cart.item_count;
				element.setAttribute('aria-label', `${cart.item_count} items in cart`);
			});
		} catch (error) {
			console.error('Update cart count error:', error);
		}
	}

	setButtonLoading(button, isLoading) {
		const span = button.querySelector('span');
		const defaultText = button.getAttribute('data-default-text') || 'Add to cart';
		
		button.disabled = isLoading;
		button.classList.toggle('opacity-50', isLoading);
		
		if (span) {
			span.textContent = isLoading 
				? (window.themeStrings?.addingToCart || 'Adding to cart...')
				: defaultText;
		}
	}
}

// Initialisation
function initCarts() {
	// Gestionnaire pour les pages panier
	document.querySelectorAll('[data-cart-page]').forEach(element => {
		new CartManager(element, 'cart');
	});

	// Gestionnaire pour les formulaires produit
	document.querySelectorAll('[data-product-form]').forEach(element => {
		new CartManager(element, 'product');
	});

	// Gestionnaire global pour l'ajout au panier
	if (!window.addToCartHandlerInstance) {
		window.addToCartHandlerInstance = new AddToCartHandler();
	}
}

// Initialisation au chargement de la page
window.addEventListener('DOMContentLoaded', initCarts);

// Export pour usage externe
window.CartManager = CartManager;
